/**
 * Interpolacja IDW (Inverse Distance Weighting) granic geologicznych.
 *
 * Każdy otwór ma granice stref: grunt/zwietrzlina/skała.
 * Dla dowolnego punktu (X, Y) interpolujemy głębokości granic
 * na podstawie odwrotności kwadratu odległości od znanych otworów.
 */

/**
 * Klasyfikacja warstwy wg profilu zwietrzelinowego.
 * Identyczna logika jak w useConnections.js → getWeatheringProfile
 */
export function getWeatheringProfile(layerName) {
  if (!layerName || layerName.trim() === '') return 'grunt';
  const name = layerName.trim();
  const upper = name.toUpperCase();
  // SM, ST → skała
  if (upper.startsWith('SM') || upper.startsWith('ST')) return 'skala';
  // Brekacja → skała
  if (upper.startsWith('BREKACJA')) return 'skala';
  // KR, KRg → zwietrzlina (było: skała)
  if (upper.startsWith('KR')) return 'zwietrzelina';
  // KW, KWg → zwietrzlina
  if (upper.startsWith('KW')) return 'zwietrzelina';
  // Reszta → grunt
  return 'grunt';
}

/**
 * Wyciąga granice stref z warstw otworu.
 * Zwraca obiekt z głębokościami spągów (dno) poszczególnych stref.
 *
 * @param {Array} layers - warstwy otworu [{name, thickness, ...}]
 * @returns {{ gruntBottom: number, zwietrzelinaBottom: number, totalDepth: number }}
 */
export function extractZoneBoundaries(layers) {
  // Prosta zasada: idziemy od góry w dół.
  // Jak pojawi się skała — wszystko poniżej to skała.
  let currentDepth = 0;
  let firstSkalaTop = null;

  // Krok 1: znajdź gdzie pierwszy raz pojawia się skała
  for (const layer of layers) {
    const profile = getWeatheringProfile(layer.name);
    if (profile === 'skala' && firstSkalaTop === null) {
      firstSkalaTop = currentDepth;
    }
    currentDepth += layer.thickness;
  }

  const totalDepth = currentDepth;

  // Jeśli brak skały — cały profil to grunt + ewentualnie zwietrzlina
  if (firstSkalaTop === null) {
    firstSkalaTop = totalDepth;
  }

  // Krok 2: powyżej skały — rozdziel grunt i zwietrzelinę
  // Zasada: od pierwszej zwietrzliny w dół — grunt → zwietrzlina
  let gruntBottom = 0;
  let zwietrzelinaBottom = null; // null = nie znaleziono zwietrzliny
  let foundZwietrzelina = false;
  currentDepth = 0;

  for (const layer of layers) {
    const bottom = currentDepth + layer.thickness;
    if (currentDepth >= firstSkalaTop) break; // już w skale

    let profile = getWeatheringProfile(layer.name);
    // Od pierwszej zwietrzliny w dół: grunt → zwietrzlina
    if (foundZwietrzelina && profile === 'grunt') {
      profile = 'zwietrzelina';
    }

    if (profile === 'grunt') {
      gruntBottom = bottom;
    } else if (profile === 'zwietrzelina') {
      if (!foundZwietrzelina) {
        foundZwietrzelina = true;
        if (gruntBottom === 0) gruntBottom = currentDepth;
      }
      zwietrzelinaBottom = bottom;
    }
    currentDepth = bottom;
  }

  // Flagi obecności stref (przed nadpisaniem null)
  const hasGrunt = gruntBottom > 0;
  const hasZwietrzelina = zwietrzelinaBottom !== null && zwietrzelinaBottom > gruntBottom;
  const hasSkala = firstSkalaTop < totalDepth;

  // Jeśli nie było zwietrzliny — granica zwietrzliny == granica gruntu
  if (zwietrzelinaBottom === null) {
    zwietrzelinaBottom = gruntBottom;
  }

  return { gruntBottom, zwietrzelinaBottom, totalDepth, hasGrunt, hasZwietrzelina, hasSkala };
}

/**
 * Klasyfikuje punkt (komórkę gridu) na zadanej rzędnej Z na strefę: grunt/zwietrzlina/skała/powietrze.
 * Bazuje na rzędnych stropów już wygenerowanego gridu (m n.p.m.) — surfaces są presence-modulated,
 * więc wyklinowane strefy mają zerową grubość. Próg `threshold` dodatkowo wymusza skip strefy
 * gdy presence < threshold (analogicznie do trybu "Dziura" w wizualizacji 3D).
 *
 * @returns {1|2|3|null} 1=grunt, 2=zwietrzlina, 3=skała, null=powietrze lub poza obszarem
 */
export function classifyAtElevation(z, elev, zwietTop, skalaTop, gruntPres = 1, zwietPres = 1, threshold = 0.05) {
  if (elev === null || elev === undefined) return null;
  if (z > elev) return null;
  const hasGrunt = gruntPres >= threshold;
  const hasZwiet = zwietPres >= threshold;
  if (z > zwietTop) {
    if (hasGrunt) return 1;
    if (hasZwiet) return 2;
    return 3;
  }
  if (z > skalaTop) {
    return hasZwiet ? 2 : 3;
  }
  return 3;
}

/**
 * Przygotowuje dane otworów do interpolacji.
 * Każdy otwór musi mieć coordX, coordY i layers.
 *
 * @param {Array} boreholes - otwory z pełnymi danymi warstw
 * @returns {Array} [{x, y, elevation, gruntBottom, zwietrzelinaBottom, name}]
 */
export function prepareBoreholeData(boreholes) {
  return boreholes
    .filter(b => b.coordX != null && b.coordY != null && b.layers && b.layers.length > 0)
    .filter(b => {
      const elev = b.depth || b.coordZ || 0;
      return elev >= 0.5; // pomijaj otwory z brakującą/zerową rzędną terenu
    })
    .map(b => {
      const boundaries = extractZoneBoundaries(b.layers);
      // Woda: najwyższe zwierciadło ustabilizowane, fallback na sączenie
      const wl = b.waterLevels || [];
      const stabilized = wl.filter(w => w.type === 'stabilized-drilled' && w.depthFrom != null);
      const seepage = wl.filter(w => w.type === 'seepage' && w.depth != null);
      const waterDepth = stabilized.length > 0
        ? Math.min(...stabilized.map(w => w.depthFrom))
        : seepage.length > 0
          ? Math.min(...seepage.map(w => w.depth))
          : null;

      return {
        x: b.coordX,
        y: b.coordY,
        elevation: b.depth || b.coordZ || 0, // rzędna terenu (m n.p.m.)
        gruntBottom: boundaries.gruntBottom,
        zwietrzelinaBottom: boundaries.zwietrzelinaBottom,
        totalDepth: boundaries.totalDepth,
        hasGrunt: boundaries.hasGrunt,
        hasZwietrzelina: boundaries.hasZwietrzelina,
        hasSkala: boundaries.hasSkala,
        name: b.name || b.nameBorehole || '?',
        waterDepth, // głębokość zwierciadła od terenu (m) lub null
        hasWater: waterDepth != null,
      };
    });
}

/**
 * Interpolacja IDW — oblicza wartość w punkcie (px, py)
 * na podstawie znanych punktów z wagą 1/d^power.
 *
 * @param {number} px - współrzędna X punktu
 * @param {number} py - współrzędna Y punktu
 * @param {Array} points - [{x, y, value}]
 * @param {number} power - wykładnik (domyślnie 2)
 * @returns {number} interpolowana wartość
 */
export function idwInterpolate(px, py, points, power = 2, topK = null) {
  // Top-K: liczymy IDW tylko z K najbliższych otworów (przyspieszenie ~10× przy minimalnej utracie dokładności).
  // Gdy topK = null lub >= liczba otworów → zachowanie identyczne jak przed optymalizacją.
  // ABY COFNĄĆ OPTYMALIZACJĘ: usuń ten blok `if (useTopK)` i zostaw tylko oryginalną pętlę poniżej.
  const useTopK = topK !== null && topK > 0 && topK < points.length;

  if (useTopK) {
    // Pre-compute squared distances
    const distsSq = new Float64Array(points.length);
    for (let i = 0; i < points.length; i++) {
      const dx = px - points[i].x;
      const dy = py - points[i].y;
      distsSq[i] = dx * dx + dy * dy;
      if (distsSq[i] < 0.01) return points[i].value; // exact match
    }
    // Partial selection sort — znajdź K najmniejszych odległości
    const idx = new Int32Array(points.length);
    for (let i = 0; i < points.length; i++) idx[i] = i;
    for (let k = 0; k < topK; k++) {
      let minPos = k;
      for (let i = k + 1; i < points.length; i++) {
        if (distsSq[idx[i]] < distsSq[idx[minPos]]) minPos = i;
      }
      const tmp = idx[k]; idx[k] = idx[minPos]; idx[minPos] = tmp;
    }
    // IDW po K najbliższych
    let numerator = 0, denominator = 0;
    for (let k = 0; k < topK; k++) {
      const i = idx[k];
      const weight = 1 / Math.pow(distsSq[i], power / 2);
      numerator += weight * points[i].value;
      denominator += weight;
    }
    return denominator > 0 ? numerator / denominator : 0;
  }

  // Pełne IDW (oryginalne, nieruszane)
  let numerator = 0;
  let denominator = 0;

  for (const pt of points) {
    const dx = px - pt.x;
    const dy = py - pt.y;
    const distSq = dx * dx + dy * dy;

    if (distSq < 0.01) {
      // Punkt prawie dokładnie na otworze
      return pt.value;
    }

    const weight = 1 / Math.pow(distSq, power / 2);
    numerator += weight * pt.value;
    denominator += weight;
  }

  return denominator > 0 ? numerator / denominator : 0;
}

/**
 * Oblicza promień wykluczenia dla otworu BEZ danej strefy.
 * Promień = połowa odległości do najbliższego otworu MAJĄCEGO tę strefę.
 *
 * @param {Object} boreholeWithout - otwór bez strefy {x, y}
 * @param {Array} boreholesWithZone - otwory z strefą [{x, y}]
 * @returns {number} promień wykluczenia (metry)
 */
function computeExclusionRadius(boreholeWithout, boreholesWithZone) {
  let minDist = Infinity;
  for (const bw of boreholesWithZone) {
    const dx = boreholeWithout.x - bw.x;
    const dy = boreholeWithout.y - bw.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < minDist) minDist = dist;
  }
  return minDist / 2;
}

/**
 * Oblicza współczynnik obecności strefy (presence) w punkcie (px, py).
 * Uwzględnia strefę wykluczenia wokół otworów bez strefy.
 *
 * Algorytm:
 * 1. Dla każdego otworu BEZ strefy oblicza promień wykluczenia R
 *    (= połowa dist do najbliższego otworu Z strefą)
 * 2. Sprawdza czy punkt P jest w strefie wykluczenia:
 *    - dist(P, otwór_bez) < R*0.7 → presence = 0 (na pewno brak)
 *    - dist(P, otwór_bez) < R     → płynne przejście 0→1 (strefa przejściowa 0.7R..R)
 *    - dist(P, otwór_bez) >= R    → brak wykluczenia z tego otworu
 * 3. Jeśli wiele otworów bez strefy, bierze minimum presence.
 * 4. Poza wykluczeniem presence = 1.
 *
 * @param {number} px
 * @param {number} py
 * @param {Array} allPoints - [{x, y, value, hasZone}]
 * @returns {number} presence 0..1
 */
function computePresence(px, py, allPoints) {
  const withZone = allPoints.filter(p => p.hasZone);
  const withoutZone = allPoints.filter(p => !p.hasZone);

  // Wszystkie mają strefę → presence = 1
  if (withoutZone.length === 0) return 1;
  // Żaden nie ma strefy → presence = 0
  if (withZone.length === 0) return 0;

  let minPresence = 1;

  for (const bw of withoutZone) {
    const R = computeExclusionRadius(bw, withZone);
    const dx = px - bw.x;
    const dy = py - bw.y;
    const distP = Math.sqrt(dx * dx + dy * dy);

    let p;
    if (distP < R * 0.7) {
      // Wewnątrz twardej strefy wykluczenia
      p = 0;
    } else if (distP < R) {
      // Strefa przejściowa — płynne wejście (smoothstep)
      const t = (distP - R * 0.7) / (R * 0.3); // 0..1
      p = t * t * (3 - 2 * t); // smoothstep — gładkie przejście
    } else {
      p = 1;
    }

    if (p < minPresence) minPresence = p;
  }

  return minPresence;
}

/**
 * IDW z uwzględnieniem wyklinu stref.
 * Interpoluje wartość granicy strefy, mnożąc przez współczynnik obecności (presence).
 *
 * Gdy presence > 0: IDW liczone TYLKO z otworów mających strefę (unika uśredniania z zerem).
 * Gdy presence = 0: wartość = 0 (strefa nie istnieje w tym punkcie).
 *
 * @param {number} px
 * @param {number} py
 * @param {Array} allPoints - [{x, y, value, hasZone}]
 * @param {number} power - wykładnik IDW
 * @returns {{ value: number, presence: number }}
 */
export function idwInterpolateWithPresence(px, py, allPoints, power = 2, topK = null) {
  const withZone = allPoints.filter(p => p.hasZone);
  const withoutZone = allPoints.filter(p => !p.hasZone);

  // Żaden otwór nie ma strefy → brak
  if (withZone.length === 0) return { value: 0, presence: 0 };

  // Wszystkie mają strefę → zwykłe IDW
  if (withoutZone.length === 0) {
    const value = idwInterpolate(px, py, withZone, power, topK);
    return { value, presence: 1 };
  }

  const presence = computePresence(px, py, allPoints);

  if (presence < 0.001) return { value: 0, presence: 0, raw: 0 };

  // IDW tylko z otworów z strefą
  const raw = idwInterpolate(px, py, withZone, power, topK);

  return {
    value: raw * presence,
    raw,
    presence,
  };
}

/**
 * Buduje tablice punktów z flagami hasZone dla każdej strefy.
 * Unika powtarzania .map() w każdej funkcji.
 *
 * @param {Array} boreholeData - wynik prepareBoreholeData()
 * @returns {{ elevPts, gruntPts, zwietPts }}
 */
export function prepareZonePoints(boreholeData) {
  return {
    elevPts: boreholeData.map(b => ({ x: b.x, y: b.y, value: b.elevation })),
    gruntPts: boreholeData.map(b => ({
      x: b.x, y: b.y,
      value: b.gruntBottom,
      hasZone: b.hasGrunt,
    })),
    zwietPts: boreholeData.map(b => ({
      x: b.x, y: b.y,
      value: b.zwietrzelinaBottom,
      hasZone: b.hasZwietrzelina,
    })),
  };
}

/**
 * Interpoluje wirtualny otwór w punkcie (px, py).
 *
 * @param {number} px - coordX (PL-2000)
 * @param {number} py - coordY (PL-2000)
 * @param {Array} boreholeData - wynik z prepareBoreholeData()
 * @returns {{ elevation, gruntBottom, zwietrzelinaBottom, skalaEnd }}
 */
export function interpolateVirtualBorehole(px, py, boreholeData, power = 2) {
  const { elevPts, gruntPts, zwietPts } = prepareZonePoints(boreholeData);

  // Woda: IDW rzędnych bezwzględnych (m n.p.m.) tylko z otworów z hasWater — bez wyklinu.
  // Analogicznie do generateInterpolationGrid (sekcja 34.4 w docs).
  const waterPts = boreholeData
    .filter(b => b.hasWater)
    .map(b => ({ x: b.x, y: b.y, value: b.elevation - b.waterDepth }));
  const hasAnyWater = waterPts.length > 0;

  const elevation = idwInterpolate(px, py, elevPts, power);
  const gruntResult = idwInterpolateWithPresence(px, py, gruntPts, power);
  const zwietResult = idwInterpolateWithPresence(px, py, zwietPts, power);

  const gruntBottom = gruntResult.value;

  // Gdy zwietrzlina zanika (presence→0), zwietrzelinaBottom zbliża się do gruntBottom
  const zwietBotRaw = zwietResult.raw || zwietResult.value;
  let zwietrzelinaBottom = gruntBottom + (zwietBotRaw - gruntBottom) * zwietResult.presence;

  // Ograniczenie fizyczne: zwietrzlina nie może kończyć się powyżej gruntu
  if (zwietrzelinaBottom < gruntBottom) {
    zwietrzelinaBottom = gruntBottom;
  }

  // Strop skały: zwykłe IDW ze WSZYSTKICH otworów (skała nigdy nie znika)
  const zwietPtsAll = boreholeData.map(b => ({ x: b.x, y: b.y, value: b.zwietrzelinaBottom }));
  const zwietBotForSkala = idwInterpolate(px, py, zwietPtsAll, power);
  const skalaEnd = zwietBotForSkala + 10;

  // Diagnostyka — odległości i wagi per otwór
  const diagnostics = boreholeData.map(b => {
    const dx = px - b.x;
    const dy = py - b.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const weight = dist > 0.1 ? 1 / Math.pow(dist, power) : Infinity;
    return {
      name: b.name,
      dist: Math.round(dist),
      weight: weight === Infinity ? 'na otworze' : weight.toExponential(2),
      elevation: b.elevation,
      gruntBottom: b.gruntBottom,
      zwietrzelinaBottom: b.zwietrzelinaBottom,
      hasGrunt: b.hasGrunt,
      hasZwietrzelina: b.hasZwietrzelina,
      hasSkala: b.hasSkala,
    };
  }).sort((a, b) => a.dist - b.dist);

  // Woda — głębokość od terenu (m). null gdy żaden otwór nie ma wody.
  let waterDepth = null;
  let waterElevation = null;
  if (hasAnyWater) {
    waterElevation = idwInterpolate(px, py, waterPts, power);
    waterDepth = elevation - waterElevation;
    // Ograniczenie fizyczne: woda nie wyżej niż teren ani głębiej niż widoczna część otworu.
    if (waterDepth < 0) waterDepth = 0;
  }

  return {
    elevation: Math.round(elevation * 100) / 100,
    gruntBottom: Math.round(gruntBottom * 100) / 100,
    zwietrzelinaBottom: Math.round(zwietrzelinaBottom * 100) / 100,
    skalaEnd: Math.round(skalaEnd * 100) / 100,
    gruntPresence: Math.round(gruntResult.presence * 100) / 100,
    zwietPresence: Math.round(zwietResult.presence * 100) / 100,
    waterDepth: waterDepth === null ? null : Math.round(waterDepth * 100) / 100,
    waterElevation: waterElevation === null ? null : Math.round(waterElevation * 100) / 100,
    diagnostics,
  };
}

/**
 * Generuje otwory wzdłuż linii przekroju A→B.
 * Zwraca tablicę otworów gotową do importu w /editor.
 *
 * @param {Object} pointA - {x, y} współrzędne PL-2000 punktu A
 * @param {Object} pointB - {x, y} współrzędne PL-2000 punktu B
 * @param {Array} boreholeData - wynik prepareBoreholeData()
 * @param {number} count - liczba otworów wzdłuż linii (domyślnie 8)
 * @param {number} power - wykładnik IDW
 * @returns {Array} tablica otworów w formacie edytora
 */
export function generateCrossSectionBoreholes(pointA, pointB, boreholeData, count = 8, power = 2) {
  if (!boreholeData || boreholeData.length < 3 || count < 2) return [];

  const dx = pointB.x - pointA.x;
  const dy = pointB.y - pointA.y;
  const totalLength = Math.sqrt(dx * dx + dy * dy);

  const { elevPts, gruntPts, zwietPts } = prepareZonePoints(boreholeData);
  // Punkty zwietrzliny bez wyklinu — dla stropu skały (skała nigdy nie znika)
  const zwietPtsAll = boreholeData.map(b => ({ x: b.x, y: b.y, value: b.zwietrzelinaBottom }));
  // Punkty wody — rzędna bezwzględna (m n.p.m.), tylko otwory z wodą, bez wyklinu (sekcja 34.4 w docs)
  const waterPts = boreholeData
    .filter(b => b.hasWater)
    .map(b => ({ x: b.x, y: b.y, value: b.elevation - b.waterDepth }));
  const hasAnyWater = waterPts.length > 0;

  const result = [];

  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) : 0;
    const px = pointA.x + t * dx;
    const py = pointA.y + t * dy;
    const distance = t * totalLength;

    const elevation = idwInterpolate(px, py, elevPts, power);
    const gruntResult = idwInterpolateWithPresence(px, py, gruntPts, power);
    const zwietResult = idwInterpolateWithPresence(px, py, zwietPts, power);

    // Woda — IDW rzędnych bezwzględnych, głębokość od terenu
    let waterDepth = null;
    if (hasAnyWater) {
      const wElev = idwInterpolate(px, py, waterPts, power);
      const wd = elevation - wElev;
      if (wd >= 0) waterDepth = Math.round(wd * 100) / 100;
    }

    const gruntBottom = gruntResult.value;
    // Zwietrzlina z wyklinem — presence moduluje grubość
    const zwietBotRaw = zwietResult.raw || zwietResult.value;
    let zwietrzelinaBottom = gruntBottom + (zwietBotRaw - gruntBottom) * zwietResult.presence;
    if (zwietrzelinaBottom < gruntBottom) zwietrzelinaBottom = gruntBottom;

    // Strop skały: IDW ze WSZYSTKICH otworów (skała nigdy nie znika)
    const zwietBotForSkala = idwInterpolate(px, py, zwietPtsAll, power);

    // Grubości stref
    const gruntThickness = gruntResult.presence > 0.01 ? Math.max(0, Math.round(gruntBottom * 100) / 100) : 0;
    const zwietThickness = zwietResult.presence > 0.01 ? Math.max(0, Math.round((zwietrzelinaBottom - gruntBottom) * 100) / 100) : 0;
    const skalaThickness = Math.max(0.5, Math.round((zwietBotForSkala - zwietrzelinaBottom + 10) * 100) / 100);

    const layers = [];
    if (gruntThickness > 0) {
      layers.push({
        id: Date.now() + i * 10 + 1,
        name: 'Grunt',
        thickness: gruntThickness,
        color: '#8B6914',
        pattern: 'none',
        soilState: '',
        geotechnicalLayer: '',
      });
    }
    if (zwietThickness > 0) {
      layers.push({
        id: Date.now() + i * 10 + 2,
        name: 'KW',
        thickness: zwietThickness,
        color: '#DAA520',
        pattern: 'none',
        soilState: '',
        geotechnicalLayer: '',
      });
    }
    layers.push({
      id: Date.now() + i * 10 + 3,
      name: 'SM',
      thickness: skalaThickness,
      color: '#808080',
      pattern: 'none',
      soilState: '',
      geotechnicalLayer: '',
    });

    const waterLevels = [];
    if (waterDepth !== null) {
      waterLevels.push({
        id: 1,
        type: 'stabilized-drilled',
        depthFrom: waterDepth,
        depthTo: waterDepth,
        label: '1',
      });
    }

    // Hidden = punkty pośrednie (nie A ani B) — istnieją tylko jako dane dla interpolacji warstw,
    // edytor 2D pomija je przy rysowaniu słupka, etykiet, ticków, wierszy w tabeli.
    const isEndpoint = (i === 0) || (i === count - 1);

    result.push({
      id: Date.now() + i * 100,
      name: `P-${Math.round(distance)}m`,
      depth: Math.round(elevation * 100) / 100,
      distance: Math.round(distance * 100) / 100,
      x: Math.round(px * 100) / 100,
      y: Math.round(py * 100) / 100,
      coordX: Math.round(px * 100) / 100,
      coordY: Math.round(py * 100) / 100,
      waterDepth, // dla podglądu w UI przekroju
      waterLevels,
      layers,
      hidden: !isEndpoint,
    });
  }

  return result;
}

/**
 * Convex hull (otoczka wypukła) — algorytm Andrew's monotone chain, O(n log n).
 * Zwraca listę punktów otoczki w kolejności przeciwnej do ruchu wskazówek zegara.
 */
function convexHull(points) {
  if (points.length < 3) return [...points];
  const sorted = points.slice().sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (O, A, B) => (A.x - O.x) * (B.y - O.y) - (A.y - O.y) * (B.x - O.x);

  const lower = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

/** Test czy punkt jest wewnątrz polygonu (ray casting). */
function pointInPolygon(px, py, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    const intersect = ((yi > py) !== (yj > py)) &&
      (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Odległość punktu (px,py) od najbliższej krawędzi polygonu. Zwraca 0 gdy punkt wewnątrz. */
function distToPolygon(px, py, polygon) {
  if (pointInPolygon(px, py, polygon)) return 0;
  let minDistSq = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const ax = polygon[i].x, ay = polygon[i].y;
    const bx = polygon[j].x, by = polygon[j].y;
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    const ddx = px - cx, ddy = py - cy;
    const dSq = ddx * ddx + ddy * ddy;
    if (dSq < minDistSq) minDistSq = dSq;
  }
  return Math.sqrt(minDistSq);
}

/**
 * Liczy bounding box obszaru gridu na podstawie pozycji otworów + opcji kształtu.
 *
 * @param {Array} boreholeData - punkty z polami x, y
 * @param {Object} opts
 * @param {'square'|'rect'} opts.shapeMode - 'square' (kwadrat z dłuższego boku) lub 'rect' (faktyczny prostokąt)
 * @param {number|null} opts.bufferMeters - bufor w metrach z każdej strony; gdy null → 10% dłuższego boku
 * @returns {{minX, maxX, minY, maxY, rangeX, rangeY} | null}
 */
export function computeGridBoundingBox(boreholeData, opts = {}) {
  if (!boreholeData || boreholeData.length === 0) return null;
  const { shapeMode = 'square', bufferMeters = null } = opts;

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const b of boreholeData) {
    if (b.x < minX) minX = b.x;
    if (b.x > maxX) maxX = b.x;
    if (b.y < minY) minY = b.y;
    if (b.y > maxY) maxY = b.y;
  }

  const rangeXRaw = maxX - minX;
  const rangeYRaw = maxY - minY;
  const maxRange = Math.max(rangeXRaw, rangeYRaw);

  // Bufor — albo metryczny (z opcji), albo 10% dłuższego boku (legacy)
  const buffer = bufferMeters !== null ? bufferMeters : maxRange * 0.1;

  if (shapeMode === 'rect') {
    minX -= buffer;
    maxX += buffer;
    minY -= buffer;
    maxY += buffer;
  } else {
    // 'square' — wymuś kwadrat z dłuższego boku, dodaj bufor równo
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const half = maxRange / 2 + buffer;
    minX = centerX - half;
    maxX = centerX + half;
    minY = centerY - half;
    maxY = centerY + half;
  }

  return {
    minX, maxX, minY, maxY,
    rangeX: maxX - minX,
    rangeY: maxY - minY,
  };
}

/**
 * Liczy listę współrzędnych X i Y siatki dla danego bbox + rozdzielczości.
 * Wyciągnięte z generateInterpolationGrid, żeby komponent mógł przygotować punkty
 * do batchowego pobrania NMT z GUGiK ZANIM odpali generator.
 *
 * @param {Array} boreholeData - punkty z polami x, y
 * @param {number} resolution - liczba punktów na bok X
 * @param {Object} opts - { shapeMode, bufferMeters, resolutionY }
 * @returns {{ xCoords: number[], yCoords: number[], bbox } | null}
 */
export function computeGridCoordinates(boreholeData, resolution = 40, opts = {}) {
  const bbox = computeGridBoundingBox(boreholeData, opts);
  if (!bbox) return null;
  const { resolutionY = null } = opts;
  const resX = resolution;
  const resY = resolutionY !== null ? resolutionY : resolution;
  const { minX, maxX, minY, maxY } = bbox;
  const stepX = (maxX - minX) / (resX - 1);
  const stepY = (maxY - minY) / (resY - 1);
  const xCoords = [];
  const yCoords = [];
  for (let i = 0; i < resX; i++) {
    xCoords.push(Math.round((minX + i * stepX) * 100) / 100);
  }
  for (let i = 0; i < resY; i++) {
    yCoords.push(Math.round((minY + i * stepY) * 100) / 100);
  }
  return { xCoords, yCoords, bbox };
}

/**
 * Generuje grid interpolacji IDW.
 *
 * @param {Array} boreholeData - wynik prepareBoreholeData()
 * @param {number} resolution - liczba punktów na bok X (domyślnie 40)
 * @param {number} power - wykładnik IDW
 * @param {Object} opts
 * @param {'square'|'rect'} opts.shapeMode - kształt obszaru (domyślnie 'square')
 * @param {number|null} opts.bufferMeters - bufor w metrach (domyślnie null = 10%)
 * @param {number|null} opts.resolutionY - liczba punktów na bok Y (gdy null = `resolution`)
 * @param {number|null} opts.maxDistanceFromBorehole - maska danych: komórki dalej niż X m od najbliższego otworu są ucinane (null = bez maski)
 * @param {number[][]|null} opts.terrainGrid - 2D siatka rzędnych terenu z NMT [j=Y][i=X], wymiary muszą się zgadzać z xCoords/yCoords. Gdy podane → elevationSurface = terrainGrid (+ ewentualna korekta offsetowa)
 * @param {{x,y,value}[]|null} opts.terrainOffsetPoints - offset per otwór (bh.elevation - NMT_w_otworze). Gdy podane razem z terrainGrid, robi IDW offsetów i dodaje do terrainGrid → teren przechodzi dokładnie przez rzędne otworów
 * @returns {{ xCoords, yCoords, gruntSurface, zwietrzelinaSurface, skalaSurface, elevationSurface, boreholeData }}
 */
export async function generateInterpolationGrid(boreholeData, resolution = 40, power = 2, opts = {}) {
  if (!boreholeData || boreholeData.length < 3) return null;

  const {
    resolutionY = null,
    maxDistanceFromBorehole = null,
    topK = null,
    terrainGrid = null,
    terrainOffsetPoints = null,
  } = opts;
  const resX = resolution;
  const resY = resolutionY !== null ? resolutionY : resolution;
  const maskThreshold = maxDistanceFromBorehole;
  // Convex hull otworów — maska liczona od krawędzi otoczki (otworów na obrzeżu)
  const hull = maskThreshold !== null ? convexHull(boreholeData.map(b => ({ x: b.x, y: b.y }))) : null;

  const coords = computeGridCoordinates(boreholeData, resolution, opts);
  if (!coords) return null;
  const { xCoords, yCoords } = coords;

  // Walidacja terrainGrid — wymiary muszą pasować
  const useTerrainGrid = terrainGrid !== null
    && terrainGrid.length === resY
    && terrainGrid[0]?.length === resX;

  // Pre-compute data arrays for IDW (z flagami obecności stref)
  const { elevPts, gruntPts, zwietPts } = prepareZonePoints(boreholeData);
  // Punkty zwietrzliny BEZ wyklinu — dla obliczania stropu skały (skała nigdy nie znika)
  const zwietPtsAll = boreholeData.map(b => ({ x: b.x, y: b.y, value: b.zwietrzelinaBottom }));

  // Punkty wody — rzędna bezwzględna (m n.p.m.), nie głębokość od terenu
  const waterPts = boreholeData
    .filter(b => b.hasWater)
    .map(b => ({ x: b.x, y: b.y, value: b.elevation - b.waterDepth }));
  const hasAnyWater = waterPts.length > 0;

  // Surface arrays [row=Y][col=X]
  const elevationSurface = [];
  const gruntSurface = [];
  const zwietrzelinaSurface = [];
  const skalaSurface = [];
  const gruntPresenceSurface = [];
  const zwietPresenceSurface = [];
  const waterSurface = [];

  for (let j = 0; j < resY; j++) {
    // Yield co 30 rzędów żeby przeglądarka nie pokazywała "Page unresponsive"
    if (j > 0 && j % 30 === 0) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    const py = yCoords[j];
    const elevRow = [];
    const gruntRow = [];
    const zwietRow = [];
    const skalaRow = [];
    const gruntPresRow = [];
    const zwietPresRow = [];
    const waterRow = [];

    for (let i = 0; i < resX; i++) {
      const px = xCoords[i];

      // Maska danych — odległość od krawędzi convex hull otworów (otworów na obrzeżu).
      // Wewnątrz otoczki dist=0 (zawsze widoczne), na zewnątrz dist=odległość prostopadła do najbliższej krawędzi hulla.
      if (hull !== null) {
        const distToHull = distToPolygon(px, py, hull);
        if (distToHull > maskThreshold) {
          elevRow.push(null);
          gruntRow.push(null);
          zwietRow.push(null);
          skalaRow.push(null);
          gruntPresRow.push(0);
          zwietPresRow.push(0);
          waterRow.push(null);
          continue;
        }
      }

      // Rzędna terenu: NMT z GUGiK (jeśli dostarczone) + IDW korekty offsetowej, inaczej IDW z otworów
      let elev;
      if (useTerrainGrid) {
        const nmtVal = terrainGrid[j][i];
        if (nmtVal === null || nmtVal === undefined) {
          // NMT brakuje w tym punkcie (np. fail batcha) → fallback do IDW z otworów
          elev = idwInterpolate(px, py, elevPts, power, topK);
        } else if (terrainOffsetPoints && terrainOffsetPoints.length > 0) {
          // Korekta offsetem — wymuś przejście przez rzędne otworów (analogicznie do GeologicalProfile sekcja 35.3)
          const offsetCorrection = idwInterpolate(px, py, terrainOffsetPoints, power, topK);
          elev = nmtVal + offsetCorrection;
        } else {
          elev = nmtVal;
        }

        // Snap-to-borehole: w pobliżu otworu wymuś dokładne przejście przez b.elevation.
        // Smoothstep zanik od dist=0 (waga 1) do dist=snapR (waga 0). snapR = 1.5× kroku siatki.
        const stepX = xCoords[1] - xCoords[0];
        const stepY = yCoords[1] - yCoords[0];
        const snapR = 1.5 * Math.max(stepX, stepY);
        const snapRSq = snapR * snapR;
        let bestBh = null;
        let bestDistSq = snapRSq;
        for (const b of boreholeData) {
          const dx = px - b.x;
          const dy = py - b.y;
          const dSq = dx * dx + dy * dy;
          if (dSq < bestDistSq) { bestDistSq = dSq; bestBh = b; }
        }
        if (bestBh) {
          const t = 1 - Math.sqrt(bestDistSq) / snapR;
          const w = t * t * (3 - 2 * t); // smoothstep
          elev = (1 - w) * elev + w * bestBh.elevation;
        }
      } else {
        elev = idwInterpolate(px, py, elevPts, power, topK);
      }
      const gruntResult = idwInterpolateWithPresence(px, py, gruntPts, power, topK);
      const zwietResult = idwInterpolateWithPresence(px, py, zwietPts, power, topK);

      const gruntBot = gruntResult.value;

      // Strop zwietrzliny z wyklinem — dla wyświetlania granicy zwietrzliny
      const zwietBotRaw = zwietResult.raw || zwietResult.value;
      const zwietBot = gruntBot + (zwietBotRaw - gruntBot) * zwietResult.presence;

      // Strop skały: ZAWSZE zwykłe IDW ze WSZYSTKICH otworów (skała nigdy nie znika).
      // Dla otworów bez zwietrzliny: zwietrzelinaBottom == gruntBottom,
      // więc IDW naturalnie podnosi strop skały w ich rejonie.
      const zwietBotForSkala = idwInterpolate(px, py, zwietPtsAll, power, topK);

      const stropGruntu = Math.round(elev * 100) / 100;
      const stropZwietrzliny = Math.round((elev - gruntBot) * 100) / 100;
      const stropSkaly = Math.round((elev - zwietBotForSkala) * 100) / 100;

      elevRow.push(stropGruntu);
      gruntRow.push(stropGruntu); // strop gruntu = teren
      zwietRow.push(stropZwietrzliny);
      skalaRow.push(stropSkaly);
      gruntPresRow.push(Math.round(gruntResult.presence * 100) / 100);
      zwietPresRow.push(Math.round(zwietResult.presence * 100) / 100);

      // Woda: IDW rzędnych bezwzględnych (m n.p.m.) — tylko z otworów z wodą
      if (hasAnyWater) {
        const waterElev = idwInterpolate(px, py, waterPts, power, topK);
        waterRow.push(Math.round(waterElev * 100) / 100);
      } else {
        waterRow.push(null);
      }
    }

    elevationSurface.push(elevRow);
    gruntSurface.push(gruntRow);
    zwietrzelinaSurface.push(zwietRow);
    skalaSurface.push(skalaRow);
    gruntPresenceSurface.push(gruntPresRow);
    zwietPresenceSurface.push(zwietPresRow);
    waterSurface.push(waterRow);
  }

  return {
    xCoords,
    yCoords,
    elevationSurface,
    gruntSurface,
    zwietrzelinaSurface,
    skalaSurface,
    gruntPresenceSurface,
    zwietPresenceSurface,
    waterSurface: hasAnyWater ? waterSurface : null,
    boreholeData,
  };
}

/**
 * Generuje model wokselowy 3D z interpolacji IDW.
 * Każdy woksel ma typ: 'grunt', 'zwietrzelina', 'skala' lub null (powietrze).
 *
 * @param {Array} boreholeData - wynik prepareBoreholeData()
 * @param {number} resXY - rozdzielczość X (i Y gdy `opts.resY` nie podane)
 * @param {number} resZ - rozdzielczość Z (domyślnie 20)
 * @param {number} power - wykładnik IDW
 * @param {Object} opts - { shapeMode, bufferMeters, resY }
 * @returns {{ voxels: {x,y,z,type}[], xCoords, yCoords, zCoords, stats, boreholeData }}
 */
export async function generateVoxelGrid(boreholeData, resXY = 25, resZ = 20, power = 2, opts = {}) {
  if (!boreholeData || boreholeData.length < 3) return null;

  const { resY: resYOpt = null } = opts;
  const resX = resXY;
  const resY = resYOpt !== null ? resYOpt : resXY;

  // Granice Z z otworów
  let minElev = Infinity, maxElev = -Infinity;
  for (const b of boreholeData) {
    const top = b.elevation;
    const bottom = b.elevation - b.zwietrzelinaBottom - 5; // 5m poniżej najgłębszej strefy
    if (top > maxElev) maxElev = top;
    if (bottom < minElev) minElev = bottom;
  }

  // Bounding box XY według opcji kształtu i bufora
  const bbox = computeGridBoundingBox(boreholeData, opts);
  if (!bbox) return null;
  const { minX, maxX, minY, maxY } = bbox;

  // Margines Z
  const marginZ = (maxElev - minElev) * 0.05;
  minElev -= marginZ;
  maxElev += marginZ;

  const stepX = (maxX - minX) / (resX - 1);
  const stepY = (maxY - minY) / (resY - 1);
  const stepZ = (maxElev - minElev) / (resZ - 1);

  const xCoords = [];
  const yCoords = [];
  const zCoords = [];
  for (let i = 0; i < resX; i++) xCoords.push(Math.round((minX + i * stepX) * 100) / 100);
  for (let j = 0; j < resY; j++) yCoords.push(Math.round((minY + j * stepY) * 100) / 100);
  for (let k = 0; k < resZ; k++) zCoords.push(Math.round((minElev + k * stepZ) * 100) / 100);

  // Pre-compute IDW points (z flagami hasZone dla wyklinu)
  const { elevPts, gruntPts, zwietPts } = prepareZonePoints(boreholeData);
  // Punkty zwietrzliny bez wyklinu — dla stropu skały (skała nigdy nie znika)
  const zwietPtsAll = boreholeData.map(b => ({ x: b.x, y: b.y, value: b.zwietrzelinaBottom }));
  // Punkty wody — rzędna bezwzględna (m n.p.m.); analogicznie do generateInterpolationGrid
  const waterPts = boreholeData
    .filter(b => b.hasWater)
    .map(b => ({ x: b.x, y: b.y, value: b.elevation - b.waterDepth }));
  const hasAnyWater = waterPts.length > 0;

  // Powierzchnia wody [row=Y][col=X] (m n.p.m.) — null gdy żaden otwór nie ma wody
  const waterSurface = hasAnyWater ? [] : null;

  // Cache interpolowanych granic per (i,j) — liczymy raz, używamy dla każdego Z
  const boundaryCache = [];
  for (let j = 0; j < resY; j++) {
    // Yield co 20 rzędów żeby przeglądarka nie pokazywała "Page unresponsive"
    if (j > 0 && j % 20 === 0) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    const row = [];
    const waterRow = hasAnyWater ? [] : null;
    for (let i = 0; i < resX; i++) {
      const px = xCoords[i];
      const py = yCoords[j];
      const elev = idwInterpolate(px, py, elevPts, power);
      const gruntResult = idwInterpolateWithPresence(px, py, gruntPts, power);
      const zwietResult = idwInterpolateWithPresence(px, py, zwietPts, power);

      const gruntBot = gruntResult.value;
      const zwietBotRaw = zwietResult.raw || zwietResult.value;
      let zwietBot = gruntBot + (zwietBotRaw - gruntBot) * zwietResult.presence;
      if (zwietBot < gruntBot) zwietBot = gruntBot;

      // Strop skały: IDW ze WSZYSTKICH otworów (skała nigdy nie znika)
      const zwietBotForSkala = idwInterpolate(px, py, zwietPtsAll, power);

      row.push({
        stropGruntu: elev,
        stropZwietrzliny: gruntResult.presence > 0.01 ? elev - gruntBot : elev,
        stropSkaly: elev - zwietBotForSkala,
      });

      if (hasAnyWater) {
        const waterElev = idwInterpolate(px, py, waterPts, power);
        waterRow.push(Math.round(waterElev * 100) / 100);
      }
    }
    boundaryCache.push(row);
    if (hasAnyWater) waterSurface.push(waterRow);
  }

  // Generuj woksele
  const voxels = [];
  const stats = { grunt: 0, zwietrzelina: 0, skala: 0, powietrze: 0, total: 0 };

  for (let j = 0; j < resY; j++) {
    // Yield co 20 rzędów żeby przeglądarka nie pokazywała "Page unresponsive"
    if (j > 0 && j % 20 === 0) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    for (let i = 0; i < resX; i++) {
      const { stropGruntu, stropZwietrzliny, stropSkaly } = boundaryCache[j][i];

      for (let k = 0; k < resZ; k++) {
        const z = zCoords[k];
        stats.total++;

        let type = null;
        if (z > stropGruntu) {
          // Powietrze — powyżej terenu
          stats.powietrze++;
        } else if (z > stropZwietrzliny) {
          type = 'grunt';
          stats.grunt++;
        } else if (z > stropSkaly) {
          type = 'zwietrzelina';
          stats.zwietrzelina++;
        } else {
          type = 'skala';
          stats.skala++;
        }

        if (type) {
          voxels.push({
            x: xCoords[i],
            y: yCoords[j],
            z,
            type,
          });
        }
      }
    }
  }

  // Oblicz objętości (przybliżone)
  const voxelVolume = stepX * stepY * stepZ; // m³ per woksel
  stats.volumeGrunt = Math.round(stats.grunt * voxelVolume * 100) / 100;
  stats.volumeZwietrzelina = Math.round(stats.zwietrzelina * voxelVolume * 100) / 100;
  stats.volumeSkala = Math.round(stats.skala * voxelVolume * 100) / 100;
  stats.voxelVolume = Math.round(voxelVolume * 100) / 100;

  return {
    voxels,
    xCoords,
    yCoords,
    zCoords,
    waterSurface,
    stats,
    boreholeData,
  };
}

/**
 * Grupuje warstwy otworu na strefy: grunt, zwietrzelina, skala.
 * Zasada: od pierwszej skały w dół = skała.
 * @returns {{ grunt: Layer[], zwietrzelina: Layer[], skala: Layer[] }}
 */
function groupLayersByZone(layers) {
  const zones = { grunt: [], zwietrzelina: [], skala: [] };
  let foundSkala = false;

  for (const layer of layers) {
    if (foundSkala) {
      zones.skala.push(layer);
      continue;
    }
    const profile = getWeatheringProfile(layer.name);
    if (profile === 'skala') {
      foundSkala = true;
      zones.skala.push(layer);
    } else if (profile === 'zwietrzelina') {
      zones.zwietrzelina.push(layer);
    } else {
      zones.grunt.push(layer);
    }
  }
  return zones;
}

/**
 * Skaluje tablicę warstw do zadanej grubości strefy.
 * Zachowuje proporcje miąższości, kopiuje właściwości warstw.
 */
function scaleLayersToThickness(layers, targetThickness) {
  if (!layers.length || targetThickness <= 0) return [];
  const sourceThickness = layers.reduce((sum, l) => sum + l.thickness, 0);
  if (sourceThickness <= 0) return [];
  const ratio = targetThickness / sourceThickness;

  return layers.map((l, i) => ({
    ...l,
    id: Date.now() + i + Math.random(),
    thickness: Math.round(l.thickness * ratio * 100) / 100,
  }));
}

/**
 * Generuje warstwy wirtualnego otworu na przekroju geologicznym.
 * Opcja B: interpoluje granice stref liniowo, bierze warstwy z bliższego otworu.
 *
 * @param {Object} leftBorehole  - otwór po lewej {distance, depth, layers}
 * @param {Object} rightBorehole - otwór po prawej {distance, depth, layers}
 * @param {number} targetDistance - odległość wirtualnego otworu (metry)
 * @returns {{ layers: Layer[], depth: number }} warstwy i rzędna terenu
 */
export function generateVirtualBoreholeLayers(leftBorehole, rightBorehole, targetDistance) {
  const leftDist = leftBorehole.distance;
  const rightDist = rightBorehole.distance;
  const span = rightDist - leftDist;
  if (span <= 0) return { layers: [], depth: leftBorehole.depth };

  // t = 0..1 — pozycja między otworami
  const t = (targetDistance - leftDist) / span;

  // Interpolacja liniowa rzędnej terenu
  const depth = leftBorehole.depth + t * (rightBorehole.depth - leftBorehole.depth);

  // Granice stref obu otworów (z flagami obecności)
  const leftBounds = extractZoneBoundaries(leftBorehole.layers);
  const rightBounds = extractZoneBoundaries(rightBorehole.layers);

  // Wykrywanie brakujących stref
  const zones = ['grunt', 'zwietrzelina', 'skala'];
  const hasFlags = {
    grunt: { left: leftBounds.hasGrunt, right: rightBounds.hasGrunt },
    zwietrzelina: { left: leftBounds.hasZwietrzelina, right: rightBounds.hasZwietrzelina },
    skala: { left: leftBounds.hasSkala, right: rightBounds.hasSkala },
  };
  const missingCount = zones.filter(z => hasFlags[z].left !== hasFlags[z].right).length;

  // Interpolacja grubości strefy z uwzględnieniem wyklinu
  // 1 brakująca strefa → trójkąt (pojawia się od t=0.5)
  // 2+ brakujących stref → trapez (pojawia się od t=0.5 z pełną grubością)
  function wedgeThickness(leftVal, rightVal, leftHas, rightHas, t) {
    if (leftHas && rightHas) {
      return leftVal + t * (rightVal - leftVal);
    }
    if (!leftHas && !rightHas) {
      return 0;
    }
    if (leftHas && !rightHas) {
      // Strefa zanika ku prawej — do połowy istnieje, potem 0
      if (t > 0.5) return 0;
      if (missingCount === 1) {
        // Trójkąt: liniowy zanik od leftVal do 0 w t=0.5
        return leftVal * (1 - t / 0.5);
      } else {
        // Trapez: pełna grubość do t=0.5, potem 0
        return leftVal;
      }
    }
    // !leftHas && rightHas — strefa pojawia się od połowy
    if (t < 0.5) return 0;
    if (missingCount === 1) {
      // Trójkąt: liniowy wzrost od 0 w t=0.5 do rightVal
      return rightVal * ((t - 0.5) / 0.5);
    } else {
      // Trapez: liniowy wzrost od 0 w t=0.5 do rightVal w t=1
      return rightVal * ((t - 0.5) / 0.5);
    }
  }

  const gruntBottom = wedgeThickness(
    leftBounds.gruntBottom, rightBounds.gruntBottom,
    leftBounds.hasGrunt, rightBounds.hasGrunt, t
  );
  const zwietrzelinaBottom = wedgeThickness(
    leftBounds.zwietrzelinaBottom, rightBounds.zwietrzelinaBottom,
    leftBounds.hasZwietrzelina, rightBounds.hasZwietrzelina, t
  );
  const totalDepth = leftBounds.totalDepth + t * (rightBounds.totalDepth - leftBounds.totalDepth);

  // Bliższy otwór = źródło warstw
  const sourceBorehole = t <= 0.5 ? leftBorehole : rightBorehole;
  const sourceZones = groupLayersByZone(sourceBorehole.layers);

  // Grubości stref wirtualnego otworu
  const gruntThickness = Math.max(0, gruntBottom);
  const zwietrzelinaThickness = Math.max(0, zwietrzelinaBottom - gruntBottom);
  const skalaThickness = Math.max(0, totalDepth - zwietrzelinaBottom);

  // Skaluj warstwy z bliższego otworu do interpolowanych grubości stref
  const virtualLayers = [];

  if (gruntThickness > 0) {
    const gruntLayers = scaleLayersToThickness(sourceZones.grunt, gruntThickness);
    if (gruntLayers.length > 0) {
      virtualLayers.push(...gruntLayers);
    } else {
      virtualLayers.push({
        id: Date.now() + 100,
        name: 'Grunt',
        thickness: Math.round(gruntThickness * 100) / 100,
        color: '#8B6914',
        pattern: 'none',
        soilState: '',
        geotechnicalLayer: '',
      });
    }
  }

  if (zwietrzelinaThickness > 0) {
    const zwietrzelinaLayers = scaleLayersToThickness(sourceZones.zwietrzelina, zwietrzelinaThickness);
    if (zwietrzelinaLayers.length > 0) {
      virtualLayers.push(...zwietrzelinaLayers);
    } else {
      virtualLayers.push({
        id: Date.now() + 200,
        name: 'KW',
        thickness: Math.round(zwietrzelinaThickness * 100) / 100,
        color: '#DAA520',
        pattern: 'none',
        soilState: '',
        geotechnicalLayer: '',
      });
    }
  }

  if (skalaThickness > 0) {
    const skalaLayers = scaleLayersToThickness(sourceZones.skala, skalaThickness);
    if (skalaLayers.length > 0) {
      virtualLayers.push(...skalaLayers);
    } else {
      virtualLayers.push({
        id: Date.now() + 300,
        name: 'SM',
        thickness: Math.round(skalaThickness * 100) / 100,
        color: '#808080',
        pattern: 'none',
        soilState: '',
        geotechnicalLayer: '',
      });
    }
  }

  return {
    layers: virtualLayers,
    depth: Math.round(depth * 100) / 100,
  };
}
